import React, { useState, useEffect } from 'react'
import {
  Card,
  Col,
  Row,
  Pagination,
  Descriptions,
  Form,
  Select,
  Input,
  Button,
  Modal,
  message,
  List,
  Drawer,
  Tag,
  Image
} from 'antd'
import { InstagramOutlined, FacebookOutlined } from '@ant-design/icons'
import styled from 'styled-components'
import api from '../utils/api'
import { PHOTO_URL } from '../utils/apiutils'
import { useAuth } from '../hooks/AuthContext'
import { categories } from '../utils/utils'

const { Option } = Select
const { TextArea } = Input

const Container = styled.div``

const FilterForm = styled(Form)`
  margin-bottom: 24px;
`

const FilterDrawer = styled(Drawer)`
  .ant-drawer-body {
    padding: 16px;
  }
`

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
`

const Title = styled.h1`
  font-size: 24px;
  margin: 0;
`

const FilterButton = styled(Button)`
  background-color: #1890ff;
  color: white;
`

const ModelList = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  gap: 16px;
`

const CustomCard = styled.div`
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  display: flex;
  flex-direction: column;
`

const ModelImage = styled.img`
  width: 100%;
  height: 200px;
  object-fit: cover;
`

const ModelInfo = styled.div`
  padding: 12px;
  flex-grow: 1;
`

const ModelName = styled.h3`
  margin: 0 0 4px;
  font-size: 16px;
`

const ModelType = styled.p`
  margin: 0 0 4px;
  font-size: 14px;
  color: #666;
`

const ActionButton = styled.button`
  flex: 1;
  padding: 8px;
  border: none;
  background-color: #f0f5ff;
  color: #0050b3;
  cursor: pointer;
  border-radius: 4px;
  font-size: 14px;

  &:first-child {
    margin-right: 8px;
  }

  &:disabled {
    background-color: #d9d9d9;
    cursor: not-allowed;
  }
`

const FollowerCount = styled.p`
  margin: 0 0 4px;
  font-size: 12px;
  color: #999;
`

const AppliedTag = styled(Tag)`
  margin-top: 4px;
`

const ActionButtons = styled.div`
  display: flex;
  padding: 8px;
`

const InviteButton = styled(Button)`
  flex: 1;
  margin-right: 8px;
`

const DetailButton = styled(Button)`
  flex: 1;
`

const LoadMoreButton = styled(Button)`
  width: 100%;
  margin-top: 16px;
`

const AllModels = () => {
  const { businessId } = useAuth()
  const [models, setModels] = useState([])
  const [pagination, setPagination] = useState({})
  const [filters, setFilters] = useState({})
  const [form] = Form.useForm()
  const [recruitments, setRecruitments] = useState([])
  const [isRecruitmentModalVisible, setIsRecruitmentModalVisible] =
    useState(false)
  const [selectedModel, setSelectedModel] = useState(null)
  const [showFollowerCount, setShowFollowerCount] = useState(false)
  const [isDetailDrawerVisible, setIsDetailDrawerVisible] = useState(false)
  const [selectedModelDetail, setSelectedModelDetail] = useState(null)
  const [isFilterDrawerVisible, setFilterDrawerVisible] = useState(false)
  const getCategoryName = (types) => {
    if (typeof types === 'string') {
      return types
        .split(',')
        .map((type) => {
          const category = categories.find((cat) => cat.type === type.trim())
          return category ? category.name : type.trim()
        })
        .join(', ')
    }
    return types
  }

  const fetchModels = async (page = 1, limit = 30) => {
    try {
      const response = await api.get(`/api/business_B/${businessId}/models`, {
        params: {
          page,
          limit,
          filters: JSON.stringify(filters)
        }
      })
      setModels(response.data.data)
      setPagination(response.data.pagination)
    } catch (error) {
      console.error('獲取模特兒信息失敗:', error)
    }
  }

  useEffect(() => {
    fetchModels()
    fetchRecruitments()
  }, [filters])

  const fetchRecruitments = async () => {
    try {
      const response = await api.get(
        `/api/business_B/${businessId}/recruitments`
      )
      setRecruitments(response.data.data.filter((r) => r.status === 'active'))
    } catch (error) {
      console.log('獲取招聘信息失敗:', error)
    }
  }

  const handlePageChange = (page, pageSize) => {
    fetchModels(page, pageSize)
  }

  const onFinish = (values) => {
    setFilters(values)
  }

  const handleModelTypeChange = (value) => {
    setShowFollowerCount(
      ['modelWithFans', 'kol', 'influencer', 'modelAndInfluencer'].includes(
        value
      )
    )
  }

  const showRecruitmentModal = (model) => {
    setSelectedModel(model)
    setIsRecruitmentModalVisible(true)
  }

  const handleRecruitmentSelect = async (recruitmentId) => {
    try {
      await api.post(
        `/api/business_B/${businessId}/recruitments/${recruitmentId}/invite/${selectedModel._id}`
      )
      message.success('成功邀請應聘者')
      setIsRecruitmentModalVisible(false)
      fetchModels() // 重新獲取模特列表以更新狀態
    } catch (error) {
      console.error('邀請應聘者失敗:', error)
      message.error(error.response.data.error)
    }
  }

  const showModelDetail = (model) => {
    setSelectedModelDetail(model)
    setIsDetailDrawerVisible(true)
  }

  const renderModelCard = (model) => {
    const isInvitedToAllRecruitments = recruitments.every((recruitment) =>
      recruitment.invitedModels.includes(model._id)
    )

    return (
      <CustomCard key={model._id}>
        <ModelImage alt={model.name} src={`${model.avatar}`} />
        <ModelInfo>
          <ModelName>{model.name}</ModelName>
          <ModelType>{`${model.modelProfile.modelType}`}</ModelType>
          <FollowerCount>{`粉絲數: ${
            model.modelProfile.followerCount
              ? `${model.modelProfile.followerCount}以上`
              : '未知'
          }`}</FollowerCount>
          {recruitments.some((r) => r.applicants.includes(model._id)) && (
            <AppliedTag>已應聘</AppliedTag>
          )}
        </ModelInfo>
        <ActionButtons>
          <ActionButton
            onClick={() => showRecruitmentModal(model)}
            disabled={isInvitedToAllRecruitments}
          >
            {isInvitedToAllRecruitments ? '已邀請' : '邀請'}
          </ActionButton>
          <ActionButton onClick={() => showModelDetail(model)}>
            查看
          </ActionButton>
        </ActionButtons>
      </CustomCard>
    )
  }

  const renderModelDetail = () => (
    <Drawer
      title='應聘者詳細資料'
      placement='right'
      onClose={() => setIsDetailDrawerVisible(false)}
      open={isDetailDrawerVisible}
      width={window.innerWidth > 768 ? 600 : '100%'}
      bodyStyle={{ paddingBottom: 80 }}
    >
      {selectedModelDetail && (
        <Descriptions column={1} bordered>
          <Descriptions.Item label='姓名'>
            {selectedModelDetail.name}
          </Descriptions.Item>
          <Descriptions.Item label='用戶名'>
            {selectedModelDetail.username}
          </Descriptions.Item>
          <Descriptions.Item label='皮膚狀態'>
            {(() => {
              switch (selectedModelDetail.modelProfile.skinType) {
                case 'combination':
                  return '混合性'
                case 'dry':
                  return '偏乾'
                case 'oily':
                  return '偏油'
                default:
                  return selectedModelDetail.modelProfile.skinType
              }
            })()}
          </Descriptions.Item>
          <Descriptions.Item label='髮質'>
            {(() => {
              switch (selectedModelDetail.modelProfile.hairType) {
                case 'coarse':
                  return '粗硬'
                case 'fine':
                  return '細軟'
                case 'damaged':
                  return '受損'
                case 'normal':
                  return '適中'
                default:
                  return selectedModelDetail.modelProfile.hairType
              }
            })()}
          </Descriptions.Item>
          <Descriptions.Item label='頭髮長度'>
            {(() => {
              switch (selectedModelDetail.modelProfile.hairLength) {
                case 'short':
                  return '短'
                case 'medium':
                  return '中'
                case 'long':
                  return '長'
                default:
                  return selectedModelDetail.modelProfile.hairLength
              }
            })()}
          </Descriptions.Item>
          <Descriptions.Item label='指甲狀況'>
            {(() => {
              switch (selectedModelDetail.modelProfile.nailCondition) {
                case 'brittle':
                  return '斷裂'
                case 'normal':
                  return '正常'
                case 'ingrown':
                  return '嵌甲'
                default:
                  return selectedModelDetail.modelProfile.nailCondition
              }
            })()}
          </Descriptions.Item>
          <Descriptions.Item label='身體狀況'>
            {(() => {
              switch (selectedModelDetail.modelProfile.bodyCondition) {
                case 'pregnant':
                  return '懷孕'
                case 'breastfeeding':
                  return '哺乳'
                case 'none':
                  return '無'
                default:
                  return selectedModelDetail.modelProfile.bodyCondition
              }
            })()}
          </Descriptions.Item>
          <Descriptions.Item label='過敏情況'>
            {selectedModelDetail.modelProfile.isAllergic ? '是' : '否'}
            {selectedModelDetail.modelProfile.allergyDetails &&
              `: ${selectedModelDetail.modelProfile.allergyDetails}`}
          </Descriptions.Item>
          <Descriptions.Item label='希望參與項目類型'>
            {getCategoryName(
              selectedModelDetail.modelProfile.preferredCategories[0]
            )}
          </Descriptions.Item>
          <Descriptions.Item label='體驗類型'>
            {(() => {
              switch (selectedModelDetail.modelProfile.recruitmentType) {
                case 'experienceModal':
                  return '體驗模特兒(純體驗)'
                case 'reciprocalModel':
                  return '互惠模特兒(可幫忙宣傳與發文)'
                default:
                  return selectedModelDetail.modelProfile.recruitmentType
              }
            })()}
          </Descriptions.Item>
          <Descriptions.Item label='期望收費與否'>
            {(() => {
              switch (selectedModelDetail.modelProfile.expectPayment) {
                case 'yes':
                  return '是'
                case 'no':
                  return '否'
                case 'maybe':
                  return '不確定，看情況'
                default:
                  return selectedModelDetail.modelProfile.expectPayment
              }
            })()}
          </Descriptions.Item>
          <Descriptions.Item label='是否需要報酬'>
            {(() => {
              switch (selectedModelDetail.modelProfile.needPayment) {
                case 'yes':
                  return '是'
                case 'no':
                  return '否'
                case 'maybe':
                  return '不確定，看情況'
                default:
                  return selectedModelDetail.modelProfile.needPayment
              }
            })()}
          </Descriptions.Item>
          <Descriptions.Item label='模特類型'>
            {(() => {
              switch (selectedModelDetail.modelProfile.modelType) {
                case 'model':
                  return '素人模特兒'
                case 'modelWithFans':
                  return '自帶粉絲模特兒'
                case 'kol':
                  return 'KOL'
                case 'influencer':
                  return '網紅'
                case 'modelAndInfluencer':
                  return '模特兒兼網紅'
                case 'other':
                  return '其他'
                default:
                  return selectedModelDetail.modelProfile.modelType
              }
            })()}
          </Descriptions.Item>
          <Descriptions.Item label='粉絲規模'>
            {(() => {
              switch (selectedModelDetail.modelProfile.followerCount) {
                case '1000':
                  return '1000-5000'
                case '5000':
                  return '5000-10000'
                case '10000':
                  return '10000-50000'
                case '100000':
                  return '50000-100000'
                case '1000000':
                  return '100000以上'
                case 'other':
                  return '其他'
                default:
                  return selectedModelDetail.modelProfile.followerCount
              }
            })()}
          </Descriptions.Item>
          <Descriptions.Item label='社交媒體' span={2}>
            <Tag icon={<InstagramOutlined />}>
              {selectedModelDetail.modelProfile.instagramUrl}
            </Tag>
            <Tag icon={<FacebookOutlined />}>
              {selectedModelDetail.modelProfile.facebookUrl}
            </Tag>
          </Descriptions.Item>
          <Descriptions.Item label='自我介紹' span={2}>
            {selectedModelDetail.modelProfile.aboutMe}
          </Descriptions.Item>
          <Descriptions.Item label='照片' span={2}>
            <Image.PreviewGroup>
              {selectedModelDetail.modelProfile.photos.map((photo, index) => (
                <Image
                  key={index}
                  src={`${PHOTO_URL}${photo}`}
                  alt={`照片 ${index + 1}`}
                  width={100}
                  height={100}
                  style={{
                    objectFit: 'cover',
                    marginRight: 8
                  }}
                />
              ))}
            </Image.PreviewGroup>
          </Descriptions.Item>
        </Descriptions>
      )}
    </Drawer>
  )

  const renderRecruitmentList = () => (
    <List
      dataSource={recruitments}
      renderItem={(item) => (
        <List.Item
          key={item._id}
          actions={[
            item.invitedModels.includes(selectedModel._id) ? (
              <Button disabled>已邀請</Button>
            ) : item.applicants.includes(selectedModel._id) ? (
              <Button disabled>已應聘</Button>
            ) : (
              <Button
                type='primary'
                onClick={() => handleRecruitmentSelect(item._id)}
              >
                邀請
              </Button>
            )
          ]}
        >
          <List.Item.Meta
            title={item.title}
            description={
              <>
                <div>{`${item.description} | ${item.location.city} ${item.location.district}`}</div>
                {item.applicants.includes(selectedModel._id) && (
                  <Tag color='green'>該模特已應聘此項目</Tag>
                )}
                {item.invitedModels.includes(selectedModel._id) && (
                  <Tag color='blue'>已邀請該模特參與此項目</Tag>
                )}
              </>
            }
          />
        </List.Item>
      )}
    />
  )

  const isInvitedToAllRecruitments = (model) => {
    return recruitments.every((recruitment) =>
      recruitment.invitedModels.includes(model._id)
    )
  }

  return (
    <Container>
      <FilterDrawer
        title='篩選選項'
        placement='right'
        onClose={() => setFilterDrawerVisible(false)}
        open={isFilterDrawerVisible}
        width={320}
      >
        <FilterForm form={form} onFinish={onFinish} layout='vertical'>
          <Row gutter={16}>
            <Col span={24}>
              <Form.Item name='recruitmentType' label='體驗類型'>
                <Select>
                  <Option value='experienceModal'>體驗模特兒(純體驗)</Option>
                  <Option value='reciprocalModel'>
                    互惠模特兒(可幫忙宣傳與發文)
                  </Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item name='modelType' label='模特兒類型'>
                <Select onChange={handleModelTypeChange}>
                  <Option value='model'>素人模特兒</Option>
                  <Option value='modelWithFans'>自帶粉絲模特兒</Option>
                  <Option value='kol'>KOL</Option>
                  <Option value='influencer'>網紅</Option>
                  <Option value='modelAndInfluencer'>模特兒兼網紅</Option>
                  <Option value='other'>其他</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name='skinType' label='皮膚狀態'>
                <Select>
                  <Option value='combination'>混合性</Option>
                  <Option value='dry'>偏乾</Option>
                  <Option value='oily'>偏油</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name='hairType' label='髮質'>
                <Select>
                  <Option value='coarse'>粗硬</Option>
                  <Option value='fine'>細軟</Option>
                  <Option value='damaged'>受損</Option>
                  <Option value='normal'>適中</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name='hairVolume' label='髮量'>
                <Select>
                  <Option value='thin'>少</Option>
                  <Option value='medium'>適中</Option>
                  <Option value='thick'>多</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name='hairLength' label='頭髮長度'>
                <Select>
                  <Option value='short'>短</Option>
                  <Option value='medium'>中</Option>
                  <Option value='long'>長</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name='nailCondition' label='指甲狀況'>
                <Select>
                  <Option value='brittle'>斷裂</Option>
                  <Option value='normal'>正常</Option>
                  <Option value='ingrown'>嵌甲</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name='bodyCondition' label='身體狀況'>
                <Select>
                  <Option value='pregnant'>懷孕</Option>
                  <Option value='breastfeeding'>哺乳</Option>
                  <Option value='none'>無</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>
          {showFollowerCount && (
            <Row gutter={16}>
              <Col span={6}>
                <Form.Item name='followerCount' label='粉絲規模'>
                  <Select>
                    <Option value='1000'>1000-5000</Option>
                    <Option value='5000'>5000-10000</Option>
                    <Option value='10000'>10000-50000</Option>
                    <Option value='100000'>50000-100000</Option>
                    <Option value='1000000'>100000以上</Option>
                    <Option value='other'>其他</Option>
                  </Select>
                </Form.Item>
              </Col>
            </Row>
          )}
          <Form.Item>
            <Button type='primary' htmlType='submit'>
              篩選
            </Button>
          </Form.Item>
        </FilterForm>
      </FilterDrawer>

      <Header>
        <FilterButton onClick={() => setFilterDrawerVisible(true)}>
          篩選
        </FilterButton>
      </Header>

      <ModelList>{models.map((model) => renderModelCard(model))}</ModelList>

      <LoadMoreButton onClick={() => fetchModels(pagination.currentPage + 1)}>
        加載更多
      </LoadMoreButton>

      <Modal
        title='選擇招聘項目'
        open={isRecruitmentModalVisible}
        onCancel={() => setIsRecruitmentModalVisible(false)}
        footer={null}
      >
        {renderRecruitmentList()}
      </Modal>

      {renderModelDetail()}
    </Container>
  )
}

export default AllModels
